import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import {
  Box,
  Button,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  Typography,
  Chip,
  Alert,
  Snackbar,
  Avatar,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import { getProducts, deleteProduct, getSKUs, getMedias } from '../app/api';
import { useTranslation } from '../app/LanguageContext';
import { formatDateTime } from '../app/date';
import ConfirmDialog from '../components/ConfirmDialog';
import ListToolbar from '../components/ListToolbar';
import { PRODUCT_DEFAULT_IMAGE } from '../components/MediaGallery';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:8080';

const ProductsPage = () => {
  const { translate } = useTranslation();
  const [products, setProducts] = useState([]);
  const [productsWithSkuCount, setProductsWithSkuCount] = useState([]);
  const [productImages, setProductImages] = useState({});
  const [loading, setLoading] = useState(true);
  const [filterType, setFilterType] = useState('ALL');
  const [orderBy, setOrderBy] = useState('created_at');
  const [order, setOrder] = useState('desc');
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const [confirmDialog, setConfirmDialog] = useState({ open: false, productId: null, productName: '' });
  const navigate = useNavigate();

  const loadProducts = async () => {
    try {
      setLoading(true);
      const [productsResponse, mediasResponse] = await Promise.all([
        getProducts(),
        getMedias(),
      ]);
      const productsList = productsResponse.data.products || [];
      setProducts(productsList);

      const allMedias = mediasResponse.data.medias || [];
      const imagesMap = {};
      
      productsList.forEach((product) => {
        // Busca todas as imagens do produto
        const productMedias = allMedias
          .map((media) => {
            if (!media.metadata || media.metadata.length === 0) {
              return null;
            }
            
            const matchingMeta = media.metadata.find(
              (meta) => meta.key === 'product_image'
            );
            
            if (!matchingMeta) {
              return null;
            }
            
            // O valor já vem parseado da API (objeto ou string)
            const metaValue = matchingMeta.value;
            const isObject = typeof metaValue === 'object' && metaValue !== null;
            
            // Se não é objeto, verifica se o valor simples corresponde
            if (!isObject) {
              if (metaValue !== product.id) {
                return null;
              }
              return {
                ...media,
                order: 0,
              };
            }
            
            // Se é objeto, verifica se o id corresponde
            if (metaValue.id !== product.id) {
              return null;
            }
            
            return {
              ...media,
              order: metaValue?.order || 0,
            };
          })
          .filter((media) => media !== null)
          .sort((a, b) => (a.order || 0) - (b.order || 0));
        
        // Pega a primeira imagem (menor order)
        if (productMedias.length > 0) {
          imagesMap[product.id] = `${API_URL}${productMedias[0].file_path}`;
        }
      });
      setProductImages(imagesMap);

      const productsWithCounts = await Promise.all(
        productsList.map(async (product) => {
          try {
            const skusResponse = await getSKUs(product.id);
            return {
              ...product,
              skuCount: (skusResponse.data || []).length,
            };
          } catch (error) {
            return {
              ...product,
              skuCount: 0,
            };
          }
        })
      );

      setProductsWithSkuCount(productsWithCounts);
    } catch (error) {
      setSnackbar({
        open: true,
        message: error.message,
        severity: 'error',
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadProducts();
  }, []);

  const handleDeleteClick = (id, name) => {
    setConfirmDialog({
      open: true,
      productId: id,
      productName: name,
    });
  };

  const handleDeleteConfirm = async () => {
    const { productId } = confirmDialog;
    setConfirmDialog({ open: false, productId: null, productName: '' });

    try {
      await deleteProduct(productId);
      setSnackbar({
        open: true,
        message: translate('products.deleted'),
        severity: 'success',
      });
      loadProducts();
    } catch (error) {
      setSnackbar({
        open: true,
        message: error.message,
        severity: 'error',
      });
    }
  };

  const handleDeleteCancel = () => {
    setConfirmDialog({ open: false, productId: null, productName: '' });
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'ACTIVE':
        return 'success';
      case 'INACTIVE':
        return 'default';
      default:
        return 'default';
    }
  };

  const getStatusLabel = (status) => {
    switch (status) {
      case 'ACTIVE':
        return translate('products.active');
      case 'INACTIVE':
        return translate('products.inactive');
      default:
        return status;
    }
  };

  const getTypeLabel = (type) => {
    if (type === 'SERVICE') return translate('products.item_type_service');
    return translate('products.item_type_product');
  };

  const filteredByType =
    filterType === 'ALL'
      ? productsWithSkuCount
      : productsWithSkuCount.filter((p) => (p.type || 'PRODUCT') === filterType);

  const handleRequestSort = (columnId) => {
    if (orderBy === columnId) {
      setOrder((o) => (o === 'asc' ? 'desc' : 'asc'));
    } else {
      setOrderBy(columnId);
      const descDefault = columnId === 'created_at' || columnId === 'updated_at';
      setOrder(descDefault ? 'desc' : 'asc');
    }
  };

  const compare = (a, b) => {
    const mult = order === 'asc' ? 1 : -1;
    const cmp = (x, y) => (x < y ? -1 : x > y ? 1 : 0);
    switch (orderBy) {
      case 'created_at': {
        const da = a.created_at ? new Date(a.created_at).getTime() : 0;
        const db = b.created_at ? new Date(b.created_at).getTime() : 0;
        return mult * (da - db);
      }
      case 'updated_at': {
        const da = a.updated_at ? new Date(a.updated_at).getTime() : 0;
        const db = b.updated_at ? new Date(b.updated_at).getTime() : 0;
        return mult * (da - db);
      }
      case 'name':
        return mult * (a.name || '').localeCompare(b.name || '', undefined, { sensitivity: 'base' });
      case 'code':
        return mult * (a.code || '').localeCompare(b.code || '', undefined, { sensitivity: 'base' });
      case 'type':
        return mult * (String(a.type || '')).localeCompare(String(b.type || ''), undefined, { sensitivity: 'base' });
      case 'status':
        return mult * (String(a.status || '')).localeCompare(String(b.status || ''), undefined, { sensitivity: 'base' });
      case 'skuCount':
        return mult * cmp(Number(a.skuCount) || 0, Number(b.skuCount) || 0);
      default:
        return 0;
    }
  };

  const filteredProducts = [...filteredByType].sort(compare);

  const filterOptions = [
    { value: 'ALL', labelKey: 'products.filter_all' },
    { value: 'PRODUCT', labelKey: 'products.item_type_product' },
    { value: 'SERVICE', labelKey: 'products.item_type_service' },
  ];

  const SortableHeader = ({ columnId, labelKey, align = 'left' }) => (
    <TableCell
      sortDirection={orderBy === columnId ? order : false}
      align={align}
    >
      <TableSortLabel
        active={orderBy === columnId}
        direction={orderBy === columnId ? order : 'asc'}
        onClick={() => handleRequestSort(columnId)}
      >
        {translate(labelKey)}
      </TableSortLabel>
    </TableCell>
  );

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 3 }}>
        <Typography variant="h4" component="h1">
          {translate('products.title')}
        </Typography>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => navigate('/products/new')}
        >
          {translate('common.new')} {translate('products.title')}
        </Button>
      </Box>

      <ListToolbar
        filterValue={filterType}
        filterOptions={filterOptions}
        onFilterChange={setFilterType}
        filterLabelKey="products.item_type"
      />

      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell width="80">Imagem</TableCell>
              <SortableHeader columnId="code" labelKey="products.code" />
              <SortableHeader columnId="name" labelKey="products.name" />
              <TableCell>{translate('products.description')}</TableCell>
              <SortableHeader columnId="type" labelKey="products.item_type" />
              <SortableHeader columnId="status" labelKey="products.status" />
              <SortableHeader columnId="skuCount" labelKey="products.sku_count" align="center" />
              <SortableHeader columnId="updated_at" labelKey="products.created_updated" />
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell colSpan={8} align="center">
                  {translate('common.loading')}
                </TableCell>
              </TableRow>
            ) : filteredProducts.length === 0 ? (
              <TableRow>
                <TableCell colSpan={8} align="center">
                  {translate('products.no_products')}
                </TableCell>
              </TableRow>
            ) : (
              filteredProducts.map((product) => (
                <TableRow key={product.id}>
                  <TableCell>
                    <Avatar
                      src={productImages[product.id] || PRODUCT_DEFAULT_IMAGE}
                      alt={product.name}
                      variant="rounded"
                      sx={{ width: 56, height: 56, cursor: 'pointer' }}
                      onClick={() => navigate(`/products/${product.code || product.id}`)}
                    />
                  </TableCell>
                  <TableCell>{product.code || '-'}</TableCell>
                  <TableCell>
                    <Link to={`/products/${product.code || product.id}`} style={{ textDecoration: 'none', color: 'inherit' }}>
                      <Typography variant="body2" color="primary" sx={{ cursor: 'pointer', '&:hover': { textDecoration: 'underline' } }}>
                        {product.name}
                      </Typography>
                    </Link>
                  </TableCell>
                  <TableCell>
                    <Link to={`/products/${product.code || product.id}`} style={{ textDecoration: 'none', color: 'inherit' }}>
                      <Typography variant="body2" color="primary" sx={{ cursor: 'pointer', '&:hover': { textDecoration: 'underline' } }}>
                        {product.description}
                      </Typography>
                    </Link>
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={getTypeLabel(product.type)}
                      variant="outlined"
                      size="small"
                    />
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={getStatusLabel(product.status)}
                      color={getStatusColor(product.status)}
                      size="small"
                    />
                  </TableCell>
                  <TableCell align="center">
                    {product.skuCount || 0}
                  </TableCell>
                  <TableCell sx={{ whiteSpace: 'nowrap' }}>
                    <Typography variant="caption" display="block" color="text.secondary">
                      {translate('products.label_created')}: {formatDateTime(product.created_at) || '–'}
                    </Typography>
                    <Typography variant="caption" display="block" color="text.secondary">
                      {translate('products.label_updated')}: {formatDateTime(product.updated_at) || '–'}
                    </Typography>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>

      <ConfirmDialog
        open={confirmDialog.open}
        title="Confirmar exclusão"
        message={`Tem certeza que deseja excluir o produto "${confirmDialog.productName}"?`}
        onConfirm={handleDeleteConfirm}
        onCancel={handleDeleteCancel}
        confirmText="Excluir"
        cancelText="Cancelar"
      />

      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
      >
        <Alert
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          severity={snackbar.severity}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default ProductsPage;
